import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Settings as SettingsIcon, Bell, Lock, Globe, Palette, UserCog, Scale } from 'lucide-react';
import AgentsPage from '../agents/AgentsPage';
import UsersPage from '../users/UsersPage';
import ZendeskIntegration from '../../components/integrations/ZendeskIntegration';
import KustomerIntegration from '../../components/integrations/KustomerIntegration';
import GladlyIntegration from '../../components/integrations/GladlyIntegration';
import SlackIntegration from '../../components/integrations/SlackIntegration';
import DecagonIntegration from '../../components/integrations/DecagonIntegration';
import BusinessRulesTabContent from './BusinessRulesTabContent';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';

const allTabs = [
  { id: 'agents', label: 'Agents', icon: Users, roles: ['ADMIN', 'MANAGER', 'SUPERVISOR'] },
  { id: 'users', label: 'Users', icon: UserCog, roles: ['ADMIN'] },
  { id: 'businessRules', label: 'Business Rules', icon: Scale, roles: ['ADMIN', 'MANAGER'] },
  { id: 'general', label: 'General', icon: SettingsIcon, roles: ['ADMIN', 'MANAGER'] },
  { id: 'notifications', label: 'Notifications', icon: Bell, roles: ['ADMIN', 'MANAGER', 'SUPERVISOR'] },
  { id: 'security', label: 'Security', icon: Lock, roles: ['ADMIN', 'MANAGER', 'SUPERVISOR', 'AGENT'] },
  { id: 'integrations', label: 'Integrations', icon: Globe, roles: ['ADMIN'] },
  { id: 'appearance', label: 'Appearance', icon: Palette, roles: ['ADMIN', 'MANAGER', 'SUPERVISOR', 'AGENT'] },
];

export default function SettingsPage() {
  const user = useAuthStore((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter tabs based on user role - memoized to prevent unnecessary recalculations
  const tabs = useMemo(() => {
    return allTabs.filter((tab) => tab.roles.includes(user?.role || ''));
  }, [user?.role]);

  // Derive active tab directly from URL (single source of truth)
  const urlTab = searchParams.get('tab');
  const defaultTab = tabs[0]?.id || 'security';
  const activeTab = urlTab && tabs.find(t => t.id === urlTab) ? urlTab : defaultTab;

  // Handler to change tabs - directly updates URL
  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Manage your workspace settings and preferences</p>
      </div>

      {/* Tabs */}
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-1 p-2" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700 border-2 border-blue-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'agents' && <AgentsTabContent />}
          {activeTab === 'users' && <UsersTabContent />}
          {activeTab === 'businessRules' && <BusinessRulesTabContent />}
          {activeTab === 'general' && <GeneralTabContent />}
          {activeTab === 'notifications' && <NotificationsTabContent />}
          {activeTab === 'security' && <SecurityTabContent />}
          {activeTab === 'integrations' && <IntegrationsTabContent />}
          {activeTab === 'appearance' && <AppearanceTabContent />}
        </div>
      </div>
    </div>
  );
}

function AgentsTabContent() {
  return (
    <div className="-m-6">
      <AgentsPage />
    </div>
  );
}

function UsersTabContent() {
  return (
    <div className="-m-6">
      <UsersPage />
    </div>
  );
}

function GeneralTabContent() {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Workspace Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Workspace Name</label>
            <input
              type="text"
              defaultValue="Lentil WFM"
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none">
              <option>America/New_York (EST)</option>
              <option>America/Chicago (CST)</option>
              <option>America/Denver (MST)</option>
              <option>America/Los_Angeles (PST)</option>
              <option>UTC</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
            <select className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none">
              <option>MM/DD/YYYY</option>
              <option>DD/MM/YYYY</option>
              <option>YYYY-MM-DD</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Format</label>
            <select className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none">
              <option>12-hour (AM/PM)</option>
              <option>24-hour</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">
          Cancel
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
          Save Changes
        </button>
      </div>
    </div>
  );
}

function NotificationsTabContent() {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h3>
        <div className="space-y-4">
          {[
            { label: 'Schedule Changes', description: 'Get notified when schedules are published or modified' },
            { label: 'Staffing Alerts', description: 'Receive alerts for under-staffing or over-staffing situations' },
            { label: 'Agent Status Updates', description: 'Get notified when agents change their status' },
            { label: 'Forecast Updates', description: 'Receive updates when new forecasts are generated' },
            { label: 'Time-off Requests', description: 'Get notified of new time-off requests requiring approval' },
          ].map((item) => (
            <div key={item.label} className="flex items-start justify-between py-3">
              <div className="flex-1">
                <div className="font-medium text-gray-900">{item.label}</div>
                <div className="text-sm text-gray-600 mt-1">{item.description}</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Push Notifications</h3>
        <div className="space-y-4">
          {[
            { label: 'Desktop Notifications', description: 'Show desktop notifications for important events' },
            { label: 'Mobile Push Notifications', description: 'Receive push notifications on your mobile device' },
          ].map((item) => (
            <div key={item.label} className="flex items-start justify-between py-3">
              <div className="flex-1">
                <div className="font-medium text-gray-900">{item.label}</div>
                <div className="text-sm text-gray-600 mt-1">{item.description}</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
          Save Preferences
        </button>
      </div>
    </div>
  );
}

function SecurityTabContent() {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      alert('Password must be at least 8 characters long!');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      alert('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      console.error('Error updating password:', error);
      const message = error.response?.data?.message || 'Failed to update password. Please try again.';
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handlePasswordSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Password & Authentication</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
            <input
              type="password"
              placeholder="Enter current password"
              required
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
            <input
              type="password"
              placeholder="Enter new password"
              required
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            />
            <p className="text-sm text-gray-500 mt-1">Must be at least 8 characters long</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
            <input
              type="password"
              placeholder="Confirm new password"
              required
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="py-4 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Two-Factor Authentication</h3>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="font-medium text-gray-900">Enable 2FA</div>
            <div className="text-sm text-gray-600 mt-1">Add an extra layer of security to your account</div>
          </div>
          <button type="button" className="px-4 py-2 border-2 border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50">
            Enable
          </button>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Updating...' : 'Update Password'}
        </button>
      </div>
    </form>
  );
}

function IntegrationsTabContent() {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Integrations</h3>
        <p className="text-sm text-gray-600">Connect Lentil WFM with your contact center tools</p>
      </div>

      {/* Zendesk Integration */}
      <ZendeskIntegration />

      {/* Kustomer Integration */}
      <KustomerIntegration />

      {/* Gladly Integration */}
      <GladlyIntegration />

      {/* Slack Integration */}
      <SlackIntegration />

      {/* Decagon Integration */}
      <DecagonIntegration />

      {/* Placeholder for future integrations */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <p className="text-gray-500 font-medium">More integrations coming soon</p>
        <p className="text-sm text-gray-400 mt-1">
          Five9, Talkdesk, Salesforce, Microsoft Teams, and more
        </p>
      </div>
    </div>
  );
}

function AppearanceTabContent() {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Theme</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { id: 'light', label: 'Light', description: 'Default light theme' },
            { id: 'dark', label: 'Dark', description: 'Easy on the eyes' },
            { id: 'auto', label: 'Auto', description: 'Sync with system' },
          ].map((theme) => (
            <div
              key={theme.id}
              className="relative border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900">{theme.label}</div>
                <input type="radio" name="theme" defaultChecked={theme.id === 'light'} />
              </div>
              <div className="text-sm text-gray-600">{theme.description}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="py-4 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Accent Color</h3>
        <div className="flex items-center space-x-3">
          {['blue', 'green', 'purple', 'red', 'orange', 'pink'].map((color) => (
            <button
              key={color}
              className={`w-12 h-12 rounded-lg bg-${color}-600 hover:ring-4 hover:ring-${color}-200 transition-all`}
              title={color}
            />
          ))}
        </div>
      </div>

      <div className="py-4 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Density</h3>
        <div className="space-y-3">
          {[
            { id: 'comfortable', label: 'Comfortable', description: 'More spacing between elements' },
            { id: 'compact', label: 'Compact', description: 'Less spacing, more content visible' },
          ].map((density) => (
            <label key={density.id} className="flex items-center space-x-3 cursor-pointer">
              <input type="radio" name="density" defaultChecked={density.id === 'comfortable'} />
              <div>
                <div className="font-medium text-gray-900">{density.label}</div>
                <div className="text-sm text-gray-600">{density.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
          Save Preferences
        </button>
      </div>
    </div>
  );
}
